import apiClient, { canQuerySamsara } from '../api';

class SamsaraPollingService {
  constructor() {
    // HMR-safe: avoid duplicating state/listeners across reloads
    if (typeof window !== 'undefined' && window.__samsaraPollingSingleton) {
      return window.__samsaraPollingSingleton;
    }

    this.intervals = new Map();
    this.subscribers = new Map();
    this.isActive = true;
    this.connectionStatus = null;
    this.lastUpdate = new Date();

    // Default polling intervals (in milliseconds)
    this.defaultIntervals = {
      vehicleLocations: 30000, // 30 seconds
      safetyEvents: 60000,    // 1 minute
      drivers: 300000,        // 5 minutes
      routes: 300000,          // 5 minutes
      compliance: 600000      // 10 minutes
    };

    // Track visibility changes to pause/resume polling
    this.visibilityHandler = null;
    this.setupVisibilityHandling();

    if (typeof window !== 'undefined') {
      window.__samsaraPollingSingleton = this;
    }
  }

  setupVisibilityHandling() {
    if (typeof document === 'undefined' || this.visibilityHandler) return;
    this.visibilityHandler = () => {
      if (document.hidden) {
        this.pauseAllPolling();
      } else {
        this.resumeAllPolling();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler, { once: false });
  }

  // Check if Samsara connection is available
  async checkConnection() {
    if (!canQuerySamsara()) {
      this.connectionStatus = 'disconnected';
      return false;
    }
    try {
      const response = await apiClient.get('/samsara/connections');
      if (response?.data?.skipped) {
        this.connectionStatus = 'disconnected';
        return false;
      }
      const connections = response.data.connections || [];
      const activeConnection = connections.find(conn => conn.active);
      
      this.connectionStatus = activeConnection ? 'connected' : 'disconnected';
      return this.connectionStatus === 'connected';
    } catch (error) {
      this.connectionStatus = 'error';
      return false;
    }
  }

  // Subscribe to data updates
  subscribe(dataType, callback, options = {}) {
    const interval = options.interval || this.defaultIntervals[dataType] || 30000;
    const key = `${dataType}_${Date.now()}_${Math.random()}`;
    
    this.subscribers.set(key, {
      dataType,
      callback,
      interval,
      options,
      lastFetch: null
    });

    // Start polling if not already active
    if (!this.intervals.has(dataType)) {
      this.startPolling(dataType, interval);
    }

    // Return unsubscribe function
    return () => {
      this.unsubscribe(key);
    };
  }

  // Unsubscribe from data updates
  unsubscribe(key) {
    this.subscribers.delete(key);
    
    // Check if we should stop polling for this data type
    const hasSubscribers = Array.from(this.subscribers.values())
      .some(sub => sub.dataType === this.getDataTypeFromKey(key));
    
    if (!hasSubscribers) {
      this.stopPolling(this.getDataTypeFromKey(key));
    }
  }

  getDataTypeFromKey(key) {
    const subscriber = this.subscribers.get(key);
    return subscriber ? subscriber.dataType : null;
  }

  // Start polling for a specific data type
  startPolling(dataType, interval) {
    if (this.intervals.has(dataType)) {
      return; // Already polling
    }

    const pollFunction = async () => {
      if (!this.isActive) return;
      
      try {
        // Check connection before polling
        const isConnected = await this.checkConnection();
        if (!isConnected) {
          return;
        }

        const data = await this.fetchData(dataType);
        
        // Notify all subscribers for this data type
        this.notifySubscribers(dataType, data);
        
        this.lastUpdate = new Date();
      } catch (error) {
        // Network blips during local server restart are common — don't spam console
        if (error?.code !== 'ERR_NETWORK' && error?.message !== 'Network Error') {
          console.warn(`[SamsaraPolling] Error polling ${dataType}:`, error.message || error);
        }
        this.notifySubscribers(dataType, null, error);
      }
    };

    // Initial fetch
    pollFunction();
    
    // Set up interval
    const intervalId = setInterval(pollFunction, interval);
    this.intervals.set(dataType, intervalId);
  }

  // Stop polling for a specific data type
  stopPolling(dataType) {
    const intervalId = this.intervals.get(dataType);
    if (intervalId) {
      clearInterval(intervalId);
      this.intervals.delete(dataType);
    }
  }

  // Fetch data from API
  async fetchData(dataType) {
    const endTime = new Date().toISOString();
    const startTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(); // Last 24 hours

    switch (dataType) {
      case 'vehicleLocations':
        // Filter to only show vehicles with recent trips (last 12 months)
        // This prevents showing old/decommissioned vehicles with stale locations
        return await apiClient.get('/samsara/vehicles/locations', {
          params: { limit: 100, onlyWithRecentTrips: true }
        });
      
      case 'safetyEvents':
        return await apiClient.get(`/samsara/safety/events?startTime=${startTime}&endTime=${endTime}&limit=50`);
      
      case 'drivers':
        return await apiClient.get('/samsara/drivers?limit=100');
      
      case 'compliance':
        return await apiClient.get(`/samsara/compliance/hos-logs?startTime=${startTime}&endTime=${endTime}&limit=50`);
      
      default:
        throw new Error(`Unknown data type: ${dataType}`);
    }
  }

  // Notify subscribers of data updates
  notifySubscribers(dataType, data, error = null) {
    const subscribers = Array.from(this.subscribers.values())
      .filter(sub => sub.dataType === dataType);

    subscribers.forEach(subscriber => {
      try {
        if (error) {
          subscriber.callback(null, error);
        } else {
          subscriber.callback(data, null);
        }
      } catch (callbackError) {
        console.error(`[SamsaraPolling] Callback error for ${dataType}:`, callbackError);
      }
    });
  }

  // Pause all polling
  pauseAllPolling() {
    this.isActive = false;
    // Actually clear the intervals, don't just pause them
    this.intervals.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    this.intervals.clear();
  }

  // Resume all polling
  resumeAllPolling() {
    this.isActive = true;
    
    // Restart polling for all subscribed data types
    this.subscribers.forEach((subscriber) => {
      const { dataType, interval } = subscriber;
      if (!this.intervals.has(dataType)) {
        this.startPolling(dataType, interval);
      }
    });
  }

  // Stop all polling
  stopAllPolling() {
    this.intervals.forEach((intervalId, dataType) => {
      clearInterval(intervalId);
    });
    this.intervals.clear();
    this.subscribers.clear();
    this.isActive = false;

    if (this.visibilityHandler && typeof document !== 'undefined') {
      try { document.removeEventListener('visibilitychange', this.visibilityHandler); } catch (_) {}
      this.visibilityHandler = null;
    }
  }

  // Get polling status
  getStatus() {
    return {
      isActive: this.isActive,
      connectionStatus: this.connectionStatus,
      lastUpdate: this.lastUpdate,
      activePolling: Array.from(this.intervals.keys()),
      subscriberCount: this.subscribers.size
    };
  }

  // Manual refresh for specific data type
  async refreshData(dataType) {
    try {
      const isConnected = await this.checkConnection();
      if (!isConnected) {
        throw new Error('No active Samsara connection');
      }

      const data = await this.fetchData(dataType);
      this.notifySubscribers(dataType, data);
      return data;
    } catch (error) {
      console.error(`[SamsaraPolling] Manual refresh failed for ${dataType}:`, error);
      this.notifySubscribers(dataType, null, error);
      throw error;
    }
  }

  // Set custom polling interval
  setPollingInterval(dataType, interval) {
    if (this.intervals.has(dataType)) {
      this.stopPolling(dataType);
      this.startPolling(dataType, interval);
    }
    
    // Update default interval for future subscriptions
    this.defaultIntervals[dataType] = interval;
  }

  // Get available data types
  getAvailableDataTypes() {
    return Object.keys(this.defaultIntervals);
  }

  // Check if polling is active for a data type
  isPolling(dataType) {
    return this.intervals.has(dataType);
  }

  // Get subscriber count for a data type
  getSubscriberCount(dataType) {
    return Array.from(this.subscribers.values())
      .filter(sub => sub.dataType === dataType).length;
  }
}

// Create singleton instance
const samsaraPollingService = new SamsaraPollingService();

export default samsaraPollingService;
